import React, { useState, useEffect, useCallback } from 'react';
import { arrayOf, string, shape, number, object } from 'prop-types';
import { nanoid } from 'nanoid';

import { searchString } from '../../utils/search';
import Search from '../search';
import Checkbox from '../checkbox';
import useQueryString from '../../react-hooks/use-query-string';

function Facets({
  facets,
  heading,
  keyword,
  searchFacetLabel,
  searchFacetPlaceholder,
  showAllLabel,
  showLessLabel,
  updateFacets,
  expandedFacet
}) {
  const buttonId = `id-${nanoid()}`;
  const contentId = `id-${nanoid()}`;
  const searchId = `id-${nanoid()}`;
  const [expanded, setExpanded] = useState(expandedFacet==='true'?true:false);
  const [allShown, setAllShown] = useState(false);
  const [filteredFacets, setFilteredFacets] = useState(facets);
  const [facetsQs, setFacetsQs] = useQueryString(keyword);

  const getActiveFacets = () => {
    if (Array.isArray(facetsQs)) return facetsQs;
    return facetsQs ? [facetsQs] : [];
  };

  function onChange(value, state) {
    if (state) {
      setFacetsQs([...getActiveFacets(), value]);
    } else {
      setFacetsQs(getActiveFacets().filter((facet) => facet !== value));
    }

    updateFacets(keyword);
  }

  function onSearch(value) {
    setFilteredFacets(
      facets.filter((facet) => searchString(facet.Name, value))
    );
  }

  const clearFacets = useCallback(() => {
    setFacetsQs('');
    setExpanded(false);
  }, [setFacetsQs, setExpanded]);

  useEffect(() => {
    window.addEventListener('clearFacets', clearFacets);

    return () => window.removeEventListener('clearFacets', clearFacets);
  }, [clearFacets]);

  useEffect(() => {
    setFilteredFacets(facets);
  }, [facets]);

  useEffect(() => {
    updateFacets(keyword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="faculty-staff-listing__facets">
      <h3
        className={`faculty-staff-listing__facets-header ${
          expanded ? '' : 'faculty-staff-listing__facets-header--closed'
        }`}
      >
        <button
          aria-controls={contentId}
          aria-expanded={expanded}
          className="faculty-staff-listing__facets-header-button"
          id={buttonId}
          onClick={() => setExpanded(!expanded)}
        >
          {heading}
        </button>
      </h3>
      <div
        className="faculty-staff-listing__facets-content"
        id={contentId}
        aria-labelledby={buttonId}
        role="region"
      >
        {facets.length > 10 && (
          <Search
            buttonLabel={searchFacetLabel}
            id={searchId}
            onSearch={onSearch}
            placeholder={searchFacetPlaceholder}
            value=""
          />
        )}
        <ul className="faculty-staff-listing__facets-list">
          {filteredFacets.map(({ Name, Matches, Value, Options }, index) => (
            <>
              <li
                className="faculty-staff-listing__facets-item"
                hidden={allShown ? false : index >= 10}
                key={Value}
              >
                <Checkbox
                  checked={getActiveFacets().includes(Value)}
                  id={`${keyword}-${Value}`}
                  label={Name}
                  onChange={(state) => onChange(Value, state)}
                />
                <span className="faculty-staff-listing__facets-item-matches captions">
                  {Matches}
                </span>
              </li>
              {Options && (
                <ul className="faculty-staff-listing__facets-list">
                  {Options.map(
                    ({ Name: name, Matches: matches, Value: value }) => (
                      <li
                        className="faculty-staff-listing__facets-item"
                        key={value}
                      >
                        <Checkbox
                          checked={getActiveFacets().includes(value)}
                          id={`${keyword}-${value}`}
                          label={name}
                          onChange={(state) => onChange(value, state)}
                        />
                        <span className="faculty-staff-listing__facets-item-matches captions">
                          {matches}
                        </span>
                      </li>
                    )
                  )}
                </ul>
              )}
            </>
          ))}
        </ul>
        {filteredFacets.length > 10 && (
          <button
            className={`faculty-staff-listing__facets-show-all-button ${
              allShown
                ? 'faculty-staff-listing__facets-show-all-button--expanded'
                : ''
            }`}
            onClick={() => setAllShown(!allShown)}
          >
            {allShown ? showLessLabel : showAllLabel}
          </button>
        )}
      </div>
    </div>
  );
}

Facets.propTypes = {
  facets: arrayOf(
    shape({
      Name: string.isRequired,
      Matches: number.isRequired,
      Value: string.isRequired,
      Options: object.isRequired
    }).isRequired
  ).isRequired,
  heading: string.isRequired,
  keyword: string.isRequired,
  searchFacetLabel: string.isRequired,
  searchFacetPlaceholder: string.isRequired,
  showAllLabel: string.isRequired,
  showLessLabel: string.isRequired,
  expandedFacet: string
};

export default React.memo(Facets);
